import {
  ComponentRef,
  Directive,
  effect,
  ElementRef,
  inject,
  INJECTOR,
  input,
  isSignal,
  linkedSignal,
  model,
  OnDestroy,
  output,
  runInInjectionContext,
  signal,
  Signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { filter, map, skip, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { FocusMonitor, FocusOrigin } from '@angular/cdk/a11y';
import { Overlay, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { VirtualKeyboardComponent } from '../components/virtual-keyboard.component';
import { combineLatestWith, distinctUntilChanged, fromEvent, merge, of } from 'rxjs';
import { FormGroupDirective } from '@angular/forms';
import { VirtualKeyboardService } from '../virtual-keyboard.service';

/*
'number' = nur Integer Werte, KEINE Float werte
'decimal' = Float werte
 */
export type VirtualKeyboardInputType = 'text' | 'number' | 'decimal' | 'full';

@Directive({
  selector: '[softVirtualKeyboard]',
  providers: [],
  host: {},
})
export class VirtualKeyboardDirective implements OnDestroy {
  readonly #injector = inject(INJECTOR);
  readonly focusMonitor = inject(FocusMonitor);
  readonly overlay = inject(Overlay);

  readonly keyboardService = inject(VirtualKeyboardService);

  readonly control = inject(FormGroupDirective, { optional: true });

  readonly enabled = input(this.keyboardService.enabled(), {
    alias: 'softVirtualKeyboardEnabled',
  });

  readonly element = input<ElementRef<HTMLElement> | HTMLInputElement | undefined>(
    inject(ElementRef<any>, { optional: true }) ?? undefined
  );

  readonly #elementToMonitor = linkedSignal(() => this.element());

  readonly trigger = input<Exclude<FocusOrigin, null>[]>(
    ['keyboard', 'mouse', 'touch', 'program'],
    { alias: 'softVirtualKeyboardTrigger' }
  );

  readonly value = model<string | null>(null, { alias: 'softVirtualKeyboard' });

  readonly inputType = signal<VirtualKeyboardInputType>('full');

  readonly valueChanges$ = toObservable(this.value).pipe(
    skip(1),
    distinctUntilChanged(),
    takeUntilDestroyed()
  );

  get _element(): HTMLElement | null {
    const elem = this.#elementToMonitor();
    if (!elem) return null;
    return elem instanceof ElementRef ? elem.nativeElement : elem;
  }

  private onEnter?: (event: KeyboardEvent, closeFn: () => void) => void;
  private onDismiss?: (value: string | null) => void;

  readonly appear = output<void>({ alias: 'softVirtualKeyboardAppear' });
  readonly disappear = output<void>({ alias: 'softVirtualKeyboardDisappear' });
  readonly backdropClick = output<void>({ alias: 'softVirtualKeyboardBackdropClick' });
  readonly input = output<string>({ alias: 'softVirtualKeyboardInput' });
  readonly enter = output<void>({ alias: 'softVirtualKeyboardEnter' });

  private initialInputmode: string | null = null;

  private overlayRef?: OverlayRef;
  private keyboardComponentRef?: ComponentRef<VirtualKeyboardComponent>;

  constructor() {
    // Monitor the focus of the element and set "inputmode=none"
    toObservable(this.#elementToMonitor)
      .pipe(
        filter((element) => !!element),
        map((element) =>
          element instanceof ElementRef ? element.nativeElement : element
        ),
        combineLatestWith(toObservable(this.enabled)),
        switchMap(([element, enabled]) => {
          if (enabled) {
            const mode = element.getAttribute('inputmode') ?? '';
            this.initialInputmode =
              mode !== 'none' ? mode : (this.initialInputmode ?? '');
            element.setAttribute('inputmode', 'none');

            if (element instanceof HTMLInputElement && element?.type === 'tel') {
              element?.setAttribute('type', 'text');
              element?.setAttribute('[data-initialType]', 'tel');
            }

            return merge(
              this.focusMonitor.monitor(element),
              fromEvent(element, 'click').pipe(map(() => 'program' as FocusOrigin))
            );
          } else {
            element.setAttribute('inputmode', this.initialInputmode ?? '');

            if (
              element instanceof HTMLInputElement &&
              element?.getAttribute('[data-initialType]') === 'tel'
            ) {
              element?.setAttribute('type', 'tel');
            }

            return of(null);
          }
        }),
        withLatestFrom(toObservable(this.trigger)),
        takeUntilDestroyed()
      )
      .subscribe(([focusOrigin, trigger]) => {
        // update cursor position after each click of the user
        if (
          this.keyboardComponentRef &&
          (this._element as HTMLInputElement)?.selectionStart !== undefined
        ) {
          const selectionStartIndex = (this._element as HTMLInputElement)?.selectionStart;
          this.keyboardComponentRef.setInput('cursorIndex', selectionStartIndex ?? 0);
        }

        if (!focusOrigin || !trigger.includes(focusOrigin)) return;
        runInInjectionContext(this.#injector, () => this.openKeyboard());
      });

    // sync hardware keyboard input with software keyboard value
    effect((onCleanup) => {
      const element = this.#elementToMonitor();
      const elem = element instanceof ElementRef ? element.nativeElement : element;

      if (elem && this.enabled()) {
        elem.setAttribute('inputmode', 'none');
      }

      const keydownListener = (ev: KeyboardEvent) => {
        if (!this.keyboardComponentRef || ev.metaKey || ev.altKey || ev.ctrlKey) return;

        this.keyboardComponentRef.instance?.keyPress?.(
          ev.key,
          (ev.target as HTMLInputElement)?.selectionStart ?? undefined,
          false
        );
      };

      elem?.addEventListener?.('keydown', keydownListener);

      // remove eventListener whenever the monitored element changes
      onCleanup(() => {
        elem?.removeEventListener?.('keydown', keydownListener);
      });
    });
  }

  ngOnDestroy() {
    this.closeKeyboard(this.enabled());
  }

  register(params: {
    element: Signal<ElementRef<HTMLElement> | HTMLInputElement | undefined>;
    value: Signal<string | null>;
    valueChange: (newValue: string | null) => void;
    onEnter?: (event: KeyboardEvent, closeFn: () => void) => void;
    onDismiss?: (value: string | null) => void;
    inputType?: VirtualKeyboardInputType | Signal<VirtualKeyboardInputType>;
  }) {
    effect(
      () => {
        const inputElement = params.element();
        if (!inputElement) return;
        this.setElement(inputElement);
      },
      { injector: this.#injector }
    );

    effect(
      () => {
        const inputValue = params.value();
        untracked(() => this.value.set(inputValue));
      },
      { injector: this.#injector }
    );

    effect(
      () => {
        const inputValue = isSignal(params?.inputType) ? params?.inputType() : params?.inputType;
        if(!inputValue) return;
        untracked(() => this.inputType.set(inputValue));
      },
      { injector: this.#injector }
    );

    const externalValue$ = toObservable(params.value, { injector: this.#injector });

    this.valueChanges$
      .pipe(
        withLatestFrom(externalValue$),
        filter(([value, externalValue]) => value !== externalValue),
        map(([value]) => value),
        takeUntilDestroyed()
      )
      .subscribe((value) => {
        if (value === undefined) return;
        params.valueChange(value);
      });

    if (params?.onEnter) {
      this.onEnter = params.onEnter;
    }

    if (params?.onDismiss) {
      this.onDismiss = params.onDismiss;
    }
  }

  setElement(element: ElementRef<HTMLElement> | HTMLInputElement | undefined) {
    this.#elementToMonitor.set(element);
    const elem = element instanceof ElementRef ? element.nativeElement : element;
    if (!elem) return;
    this.initialInputmode =
      elem?.inputMode !== 'none' ? elem.inputMode : (this.initialInputmode ?? '');
    elem?.setAttribute('inputmode', 'none');

    if (elem instanceof HTMLInputElement && elem?.type === 'tel') {
      elem?.setAttribute('type', 'text');
    }
  }

  closeKeyboard(emit = true) {
    if (emit)
      this.onDismiss?.(this.value());

    this.keyboardService.closeKeyboard();

    if (this.keyboardComponentRef) {
      this.keyboardComponentRef.destroy();
      this.keyboardComponentRef = undefined;
    }

    if (this.overlayRef && this.overlayRef.hasAttached()) {
      this.overlayRef.detach();
      this.overlayRef.dispose();
      this.overlayRef = undefined;
    }
  }

  openKeyboard() {
    this.keyboardService.openKeyboard();

    if (!this.overlayRef) {
      this.overlayRef = this.overlay.create({
        maxWidth: '100%',
        minWidth: '100vw',
        disposeOnNavigation: true,
        hasBackdrop: false,
        maxHeight: '90vh',
        positionStrategy: this.getPositionStrategy(),
        scrollStrategy: this.overlay.scrollStrategies.close({ threshold: 1 }),
      });

      // Close keyboard on outside click without 'losing' the click
      // We intentionally do not rely on the default backdrop:
      // we want to be able to directly handle the backdrop click (e.g., focus the next input field)
      // Otherwise the backdrop would close, but the click (on e.g., the button) would have been lost
      this.overlayRef
        .outsidePointerEvents()
        .pipe(
          filter((event) => !!event && event.type === 'click'),
          skip(1)
        )
        .subscribe(() => this.closeKeyboard());
    }

    if (!this.keyboardComponentRef) {
      const portal = new ComponentPortal(VirtualKeyboardComponent);
      this.keyboardComponentRef = this.overlayRef.attach(portal);
      this.keyboardComponentRef.setInput('value', this.value());
      this.keyboardComponentRef.setInput('inputType', this.inputType());

      // Listen for 'enter' (handle submit)
      // fireEvent (boolean) is true whenever we should emit a KeyboardEvent
      const sub = this.keyboardComponentRef.instance.onEnter.subscribe((fireEvent) => {
        const element = this._element;
        if (!element) return;

        this.enter.emit();

        const keyboardEvent = new KeyboardEvent('keydown', { key: 'Enter' });

        if (fireEvent) {
          element.dispatchEvent(keyboardEvent);
          element.dispatchEvent(new SubmitEvent('submit'));
        }

        if (this.onEnter) {
          this.onEnter(keyboardEvent, () => this.closeKeyboard());
        }

        if (this.control && this.control?.valid) {
          this.control.onSubmit(new SubmitEvent('submit'));
          this.closeKeyboard();
        }
      });

      this.keyboardComponentRef.instance.value$
        .pipe(skip(1), takeUntil(this.overlayRef.detachments()))
        .subscribe((value) => {
          if (value === undefined) return;
          this.value.set(value);
        });

      this.keyboardComponentRef.onDestroy(() => {
        sub.unsubscribe();
        this.closeKeyboard();
      });
    }
  }

  private getPositionStrategy(): PositionStrategy {
    return this.overlay.position().global().bottom().start().end();
  }
}
